import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMount } from 'react-use';
import { amplitudeService } from 'services/amplitude';
import { gaService } from 'services/ga';
import { routingService } from 'services/routing';
import { useGetInvitations } from 'shared/data/webApp/v2';
import { getPath, Path } from 'urlBuilder';

import { ProfileSetupFormProps } from '../../../resources/components';
import { getSSOMethodForAnalytics, prepareQualificationQuestions } from '../../../resources/helpers';
import { useSetupProfileWizardQualificationQuestionsAnswers } from '../../../resources/hooks';
import { profileSetupSsoState } from '../states';
import { ProfileSetupSsoStep } from '../types';
import { useSsoRegistration } from './useSsoRegistration';

export const useProfileSetupSsoPage = () => {
    const { isLoading, handleRegister, handleRedirectAfter } = useSsoRegistration();

    const ssoData = useAtomValue(profileSetupSsoState);
    const { data: { items: invitations } = {} } = useGetInvitations({
        onSuccess: (data) => {
            if (ssoData && data.items.length === 0) {
                gaService.sendEvent('land_on_user_profile', {
                    method: getSSOMethodForAnalytics(ssoData.oauthProvider),
                });
            }
        },
    });
    const hasInvitations = Boolean(invitations?.length);

    useEffect(() => {
        if (!ssoData) {
            routingService.replace(getPath(Path.login));
        }
    }, [ssoData]);

    const profileData = useMemo(
        () => ({
            firstName: ssoData?.firstName || '',
            lastName: ssoData?.lastName || '',
            email: ssoData?.email || '',
            phone: '',
        }),
        [ssoData?.email, ssoData?.firstName, ssoData?.lastName]
    );

    const [step, setStep] = useState<ProfileSetupSsoStep>(ProfileSetupSsoStep.Profile);

    const { useCases, answerAboutGL, handleSetQualificationQuestionsAnswers } =
        useSetupProfileWizardQualificationQuestionsAnswers();

    const handleSubmit: ProfileSetupFormProps['onSubmit'] = useCallback(
        async (params) => {
            if (!ssoData) {
                return;
            }

            amplitudeService.sendData('signup: create user profile');

            const qualificationQuestionAnswers = prepareQualificationQuestions(params);

            await handleRegister({
                firstName: params.firstName,
                lastName: params.lastName,
                phone: params.phone,
                app: ssoData.app,
                email: ssoData.email,
                signUpCode: ssoData.signUpCode,
                redirectUrl: ssoData.redirectUrl,
                oauthProvider: ssoData.oauthProvider,
                startingPoint: ssoData.startingPoint,
                subscribe: Boolean(params.consentMarketingAccepted),
                qualificationQuestionsAnswers: qualificationQuestionAnswers ?? undefined,
            });

            gaService.sendEvent('trial_sign_up', {
                send_to: ['AW-458300955/fEplCOncsqYaEJu8xNoB', 'default'],
            });

            if (!hasInvitations) {
                handleSetQualificationQuestionsAnswers(qualificationQuestionAnswers);
                setStep(ProfileSetupSsoStep.PreAhaMoment);
            } else {
                handleRedirectAfter();
            }
        },
        [handleRedirectAfter, handleRegister, handleSetQualificationQuestionsAnswers, hasInvitations, ssoData]
    );

    const handlePreAhaNext = useCallback(() => {
        handleRedirectAfter();
    }, [handleRedirectAfter]);

    useMount(() => {
        if (ssoData) {
            amplitudeService.sendData('signup: land on user profile setup screen', {
                'signup type': `${ssoData.oauthProvider} sso`,
            });
        }
    });

    return {
        profileData,
        isLoading,
        hasInvitations,
        handleSubmit,
        handlePreAhaNext,
        useCases,
        answerAboutGL,
        step,
    };
};
